import { BaseComponent } from '../base-component.js';
import { api } from '../../client/api.js';
import { session } from '../../client/session.js';
import { escapeHtml, escapeAttribute } from '../../shared/utils.js';
import componentStyles from './qr-generator.css?inline';
import { toast } from '../../client/toast.js';

export class QRGenerator extends BaseComponent {
  private qrSvg = '';
  private qrDataUrl = '';
  private pageUrl = '';
  private loading = true;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadQR();
  }

  protected styles(): string {
    return componentStyles;
  }

  protected template(): string {
    if (this.loading) {
      return '<div class="loading">Generating QR code...</div>';
    }

    const safeDataUrl = this.qrDataUrl.startsWith('data:image/') ? escapeAttribute(this.qrDataUrl) : '';
    const safePageUrl = escapeHtml(this.pageUrl);

    return `
      <ag-panel>
        <ag-panel-header>
          <h2>QR Code</h2>
          <p class="description">Below you can find the generated QR code that links to your personal landing page. You can also download the QR as a PNG or SVG, as well as copy the link to your page directly to share.</p>
        </ag-panel-header>
        <ag-panel-content>
          <div class="qr-container">
            <div class="qr-code">
              <img src="${safeDataUrl}" alt="QR Code">
            </div>
            
            <div class="url-display">${safePageUrl}</div>
            
            <ag-button-group alignment="center">
              <button class="btn btn-primary" id="downloadPng">Download PNG</button>
              <button class="btn btn-secondary" id="downloadSvg">Download SVG</button>
              <button class="btn btn-secondary" id="copyUrl">Copy URL</button>
            </ag-button-group>
            
            <p class="info">
              Scan this QR code to visit your landing page, or share the URL directly.
            </p>
          </div>
        </ag-panel-content>
      </ag-panel>
    `;
  }

  protected afterRender(): void {
    this.$('#downloadPng')?.addEventListener('click', () => this.downloadPng());
    this.$('#downloadSvg')?.addEventListener('click', () => this.downloadSvg());
    this.$('#copyUrl')?.addEventListener('click', () => this.copyUrl());
  }

  private async loadQR(): Promise<void> {
    this.loading = true;

    const user = session.getUser();
    if (!user) {
      this.loading = false;
      return;
    }

    const result = await api.generateQRCode(user.slug);
    if (result.success && result.data) {
      this.qrSvg = result.data.svg;
      this.qrDataUrl = result.data.dataUrl;
      this.pageUrl = `${window.location.origin}/u/${user.slug}`;
    }

    this.loading = false;
    this.render();
    this.afterRender();
  }

  private downloadPng(): void {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 400;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);

      const link = document.createElement('a');
      link.download = 'qr-code.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = this.qrDataUrl;
  }

  private downloadSvg(): void {
    const blob = new Blob([this.qrSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'qr-code.svg';
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  }

  private async copyUrl(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.pageUrl);
      toast.success('URL copied to clipboard!');
    } catch {
      const input = document.createElement('input');
      input.value = this.pageUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      toast.success('URL copied to clipboard!');
    }
  }
}

customElements.define('qr-generator', QRGenerator);
